/**
 * BoardEditor Component
 * UI for editing snakes and ladders on the board
 */

import { useState } from 'react';
import { validateBoardConfig } from './boardConfig';

/**
 * Generate random snakes and ladders for a board
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of cols
 * @param {number} numSnakes - Number of snakes to generate
 * @param {number} numLadders - Number of ladders to generate
 * @returns {{ snakes: Array, ladders: Array }}
 */
export const generateRandomConfig = (rows, cols, numSnakes = 5, numLadders = 5) => {
    const maxPosition = rows * cols;
    const usedSquares = new Set([1, maxPosition]); // Reserve start and end

    const snakes = [];
    const ladders = [];

    // Generate ladders first (go up)
    let attempts = 0;
    while (ladders.length < numLadders && attempts < 100) {
        attempts++;
        const start = Math.floor(Math.random() * (maxPosition - 10)) + 2; // 2 to maxPosition-9
        const end = start + Math.floor(Math.random() * Math.min(30, maxPosition - start - 1)) + 5; // At least 5 squares up

        if (end >= maxPosition || usedSquares.has(start) || usedSquares.has(end)) continue;

        ladders.push({ start, end });
        usedSquares.add(start);
        usedSquares.add(end);
    }

    // Generate snakes (go down)
    attempts = 0;
    while (snakes.length < numSnakes && attempts < 100) {
        attempts++;
        const start = Math.floor(Math.random() * (maxPosition - 15)) + 15; // At least 15 to have room to go down
        const end = start - Math.floor(Math.random() * Math.min(30, start - 2)) - 5; // At least 5 squares down

        if (end < 1 || usedSquares.has(start) || usedSquares.has(end)) continue;

        snakes.push({ start, end });
        usedSquares.add(start);
        usedSquares.add(end);
    }

    return { snakes, ladders };
};

/**
 * Single snake/ladder entry editor
 */
const EntryEditor = ({ type, entry, index, maxPosition, onUpdate, onRemove }) => {
    const [start, setStart] = useState(entry.start.toString());
    const [end, setEnd] = useState(entry.end.toString());
    const [error, setError] = useState(null);

    const handleBlur = () => {
        const startNum = parseInt(start, 10);
        const endNum = parseInt(end, 10);

        if (isNaN(startNum) || isNaN(endNum)) {
            setError('Invalid numbers');
            return;
        }

        if (startNum < 1 || startNum > maxPosition || endNum < 1 || endNum > maxPosition) {
            setError(`Must be between 1 and ${maxPosition}`);
            return;
        }

        if (type === 'snake' && startNum <= endNum) {
            setError('Snake start must be > end');
            return;
        }

        if (type === 'ladder' && startNum >= endNum) {
            setError('Ladder start must be < end');
            return;
        }

        setError(null);
        onUpdate(index, { start: startNum, end: endNum });
    };

    const isSnake = type === 'snake';

    return (
        <div className="flex items-center gap-2 py-1">
            <span className={`text-xs font-medium w-6 ${isSnake ? 'text-badger-purple' : 'text-yellow-600'}`}>
                {index + 1}.
            </span>
            <input
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                onBlur={handleBlur}
                min={1}
                max={maxPosition}
                className="w-14 text-sm border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-badger-purple"
                placeholder="Start"
            />
            <span className="text-gray-400">→</span>
            <input
                type="number"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                onBlur={handleBlur}
                min={1}
                max={maxPosition}
                className="w-14 text-sm border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-badger-purple"
                placeholder="End"
            />
            <button
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                title="Remove"
            >
                ×
            </button>
            {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
    );
};

/**
 * BoardEditor Component
 */
export const BoardEditor = ({
    snakes,
    ladders,
    rows,
    cols,
    onConfigChange,
    onReset
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const maxPosition = rows * cols;

    const handleSnakeUpdate = (index, newEntry) => {
        const newSnakes = [...snakes];
        newSnakes[index] = newEntry;
        validateAndUpdate(newSnakes, ladders);
    };

    const handleLadderUpdate = (index, newEntry) => {
        const newLadders = [...ladders];
        newLadders[index] = newEntry;
        validateAndUpdate(snakes, newLadders);
    };

    const handleSnakeRemove = (index) => {
        const newSnakes = snakes.filter((_, i) => i !== index);
        validateAndUpdate(newSnakes, ladders);
    };

    const handleLadderRemove = (index) => {
        const newLadders = ladders.filter((_, i) => i !== index);
        validateAndUpdate(snakes, newLadders);
    };

    const handleAddSnake = () => {
        // Find unused positions
        const used = new Set([
            ...snakes.flatMap(s => [s.start, s.end]),
            ...ladders.flatMap(l => [l.start, l.end])
        ]);

        let start = Math.floor(maxPosition * 0.7);
        let end = Math.floor(maxPosition * 0.3);

        while (used.has(start) && start < maxPosition) start++;
        while (used.has(end) && end > 1) end--;

        if (start > end && !used.has(start) && !used.has(end)) {
            validateAndUpdate([...snakes, { start, end }], ladders);
        }
    };

    const handleAddLadder = () => {
        // Find unused positions
        const used = new Set([
            ...snakes.flatMap(s => [s.start, s.end]),
            ...ladders.flatMap(l => [l.start, l.end])
        ]);

        let start = Math.floor(maxPosition * 0.2);
        let end = Math.floor(maxPosition * 0.6);

        while (used.has(start) && start > 1) start--;
        while (used.has(end) && end < maxPosition) end++;

        if (start < end && !used.has(start) && !used.has(end)) {
            validateAndUpdate(snakes, [...ladders, { start, end }]);
        }
    };

    const handleRandomize = () => {
        const numSnakes = Math.max(3, Math.floor(maxPosition / 15));
        const numLadders = Math.max(3, Math.floor(maxPosition / 15));
        const { snakes: newSnakes, ladders: newLadders } = generateRandomConfig(rows, cols, numSnakes, numLadders);
        validateAndUpdate(newSnakes, newLadders);
    };

    const validateAndUpdate = (newSnakes, newLadders) => {
        const config = { rows, cols, snakes: newSnakes, ladders: newLadders };
        const validation = validateBoardConfig(config);

        if (validation.valid) {
            setValidationErrors([]);
            onConfigChange(newSnakes, newLadders);
        } else {
            setValidationErrors(validation.errors);
            // Still update to show the invalid state
            onConfigChange(newSnakes, newLadders);
        }
    };

    return (
        <div className="border-t border-gray-200 pt-4 mt-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
                <span>{isOpen ? '−' : '+'}</span>
                <span>Edit Board</span>
            </button>

            {isOpen && (
                <div className="mt-3 space-y-4 bg-gray-50 p-4 rounded-lg">
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={handleRandomize}
                            className="px-3 py-1 text-sm bg-badger-purple text-white rounded hover:bg-purple-700 transition-colors"
                        >
                            Randomize
                        </button>
                        <button
                            onClick={onReset}
                            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        >
                            Reset to Default
                        </button>
                    </div>

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs text-red-600 font-medium">Validation errors:</p>
                            <ul className="text-xs text-red-500 list-disc list-inside">
                                {validationErrors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Snakes */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-badger-purple">
                                Snakes ({snakes.length})
                            </h4>
                            <button
                                onClick={handleAddSnake}
                                className="text-xs text-badger-purple hover:underline"
                            >
                                + Add Snake
                            </button>
                        </div>
                        <div className="space-y-1">
                            {snakes.map((snake, index) => (
                                <EntryEditor
                                    key={`snake-${index}`}
                                    type="snake"
                                    entry={snake}
                                    index={index}
                                    maxPosition={maxPosition}
                                    onUpdate={handleSnakeUpdate}
                                    onRemove={handleSnakeRemove}
                                />
                            ))}
                            {snakes.length === 0 && (
                                <p className="text-xs text-gray-400 italic">No snakes</p>
                            )}
                        </div>
                    </div>

                    {/* Ladders */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-yellow-600">
                                Ladders ({ladders.length})
                            </h4>
                            <button
                                onClick={handleAddLadder}
                                className="text-xs text-yellow-600 hover:underline"
                            >
                                + Add Ladder
                            </button>
                        </div>
                        <div className="space-y-1">
                            {ladders.map((ladder, index) => (
                                <EntryEditor
                                    key={`ladder-${index}`}
                                    type="ladder"
                                    entry={ladder}
                                    index={index}
                                    maxPosition={maxPosition}
                                    onUpdate={handleLadderUpdate}
                                    onRemove={handleLadderRemove}
                                />
                            ))}
                            {ladders.length === 0 && (
                                <p className="text-xs text-gray-400 italic">No ladders</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BoardEditor;
